import React from "react";
import { AlertCircle, ClipboardList } from "lucide-react";

type Bucket = {
  count: number;
  label: string;
  subtitle: string;
  show: boolean;
};

type HomeworkSummary = {
  needsAttention?: Bucket;
  openHomework?: Bucket;
  totalIncompleteCount?: number;
  updatedAt?: string;
};

type Props = {
  summary: HomeworkSummary | null;
  onNeedsAttentionClick?: () => void;
  onOpenHomeworkClick?: () => void;
};

export default function HomeworkSummaryRow({
  summary,
  onNeedsAttentionClick,
  onOpenHomeworkClick,
}: Props) {
  const needs = summary?.needsAttention;
  const open = summary?.openHomework;

  const showNeeds = needs?.show === true && Number(needs.count) > 0;
  const showOpen = open?.show === true && Number(open.count) > 0;

  if (!showNeeds && !showOpen) return null;

  const onlyOne = (showNeeds ? 1 : 0) + (showOpen ? 1 : 0) === 1;

  return (
    <div className="flex justify-center pt-4">
      <div className={`grid w-full max-w-3xl gap-2 px-4 ${onlyOne ? "grid-cols-1" : "grid-cols-2"}`}>
        {showNeeds && (
          <button
            type="button"
            onClick={onNeedsAttentionClick}
            className="min-w-0 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-left shadow-sm active:scale-[0.99]"
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                <AlertCircle className="h-4 w-4" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate text-sm font-semibold text-gray-900">
                    <span className="sm:hidden">Due</span>
                    <span className="hidden sm:inline">{needs.label || "Needs attention"}</span>
                  </div>
                  <div className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 px-2 text-xs font-bold text-white">
                    {needs.count}
                  </div>
                </div>

                <div className="mt-0.5 truncate text-[11px] leading-4 text-gray-600">
                  {needs.subtitle}
                </div>
              </div>
            </div>
          </button>
        )}

        {showOpen && (
          <button
            type="button"
            onClick={onOpenHomeworkClick}
            className="min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-left shadow-sm active:scale-[0.99]"
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700">
                <ClipboardList className="h-4 w-4" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate text-sm font-semibold text-gray-900">
                    <span className="sm:hidden">All</span>
                    <span className="hidden sm:inline">{open.label || "All homework"}</span>
                  </div>
                  <div className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-gray-500 px-2 text-xs font-bold text-white">
                    {open.count}
                  </div>
                </div>

                <div className="mt-0.5 truncate text-[11px] leading-4 text-gray-600">
                  {open.subtitle}
                </div>
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}